import AddIcon from '@mui/icons-material/Add';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';

import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import BusTourListMobileLayout, { BusTourMobileActionButton, BusTourMobileListItem } from './BusTourListMobile';
import BusTourListSearchField, { busTourListToolbarButtonSx, busTourListToolbarRowSx } from './BusTourListSearchField';
import StopWarningIcon from './StopWarningIcon';
import { useBusTourAddLabel } from './useBusTourAddLabel';

const useStyles = makeStyles((theme) => ({
    recordList: {
        height: `calc(${theme.cp.contentHeight} - 6rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
}));

const BusStopList = ({ onRefreshRef, ...props }) => {
    const history = useHistory();
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const classes = useStyles();
    const isSmallScreen = !useIsWidthUp('sm');

    const [syncActive, setSyncActive] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);

    React.useEffect(() => {
        if (onRefreshRef) onRefreshRef.current = () => setLastUpdate(new Date().toString());
    }, [onRefreshRef]);
    const urlParams = new URLSearchParams(window.location.search);
    const [query, setQuery] = React.useState(urlParams.get('query') || null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const addButtonLabel = useBusTourAddLabel('NEW BUS STOP');

    const resource = 'route_stop';

    const onQueryChange = (newQuery) => {
        setQuery(newQuery);
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('query', newQuery);
        history.push(`${window.location.pathname}?${searchParams.toString()}`);
    };

    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['bus stop', 'bus stops'], onDeleted: refreshList });
    const doDelete = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setSelectedIds([]);
                    setIdsToDelete(null);
                }
            })
            .finally(() => setSyncActive(false));
    };

    const listItemRenderer = React.useCallback(
        (row, idx, rows) => {
            const location = row.latitude && row.longitude ? `${row.latitude}, ${row.longitude}` : '—';
            const openRow = () => history.push(`/bus-tours/bus-stops/${row.id}`);

            return (
                <BusTourMobileListItem
                    key={row.id}
                    name={<>{row.name}<StopWarningIcon stop={row} /></>}
                    onOpen={openRow}
                    isLast={idx >= rows.length - 1}
                    details={
                        <>
                            {row.description ? (
                                <Typography variant="body2" color="text.secondary" display="block" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                    {row.description}
                                </Typography>
                            ) : null}
                            <Typography variant="body2" color="text.secondary" display="block">
                                Location: {location}
                            </Typography>
                        </>
                    }
                    actions={
                        <>
                            <Grid item>
                                <IconButton
                                    size="small"
                                    component={RouterLink}
                                    to={`/bus-tours/bus-stops/${row.id}`}
                                    onClick={(ev) => ev.stopPropagation()}
                                    aria-label="Edit bus stop"
                                >
                                    <BorderColorOutlinedIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <DeleteIconButton
                                    title="Delete bus stop"
                                    perm="vehicle_fleet.delete"
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        setIdsToDelete([row.id]);
                                    }}
                                />
                            </Grid>
                        </>
                    }
                />
            );
        },
        [history]
    );

    const busStopsRecordList = (
        <RecordList
            className={classes.recordList}
            resource={resource}
            disableSelectionOnClick
            sortModel={[{ field: 'name', sort: 'asc' }]}
            deps={lastUpdate}
            query={query}
            selectionModel={selectedIds}
            onSelectionModelChange={(selectionModel, selection) => setSelectedIds(selection || [])}
            listItemRenderer={isSmallScreen ? listItemRenderer : undefined}
            mobileNaturalHeight={isSmallScreen}
            noMatchesLabel="No bus stops"
            noItemsFoundMessage="No bus stops"
            columns={[
                ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                {
                    field: 'name',
                    headerName: 'Name',
                    sortable: true,
                    flex: 0.5,
                    renderCell: (p) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.value}</Box>
                            <StopWarningIcon stop={p.row} />
                        </Box>
                    ),
                },
                {
                    field: 'description',
                    headerName: 'Description',
                    sortable: false,
                    flex: 1,
                    valueGetter: (p) => p.row.description || '',
                },
                {
                    field: 'location',
                    headerName: 'Location',
                    sortable: false,
                    width: 200,
                    valueGetter: (p) => {
                        if (p.row.latitude && p.row.longitude) {
                            return `${p.row.latitude}, ${p.row.longitude}`;
                        }
                        return '';
                    },
                },
                {
                    field: 'buttons',
                    headerName: 'Action',
                    sortable: false,
                    width: 100,
                    renderCell: (p) => (
                        <Grid container alignItems="center">
                            <Grid item>
                                {/* No stopPropagation (CP-8408) — see AttractionList: a swallowed click leaves the
                                    grid's stale cell to steal focus from the edit drawer on the first click. */}
                                <IconButton size="small" component={RouterLink} to={`/bus-tours/bus-stops/${p.id}`}>
                                    <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <DeleteIconButton perm="vehicle_fleet.delete" onClick={() => setIdsToDelete([p.id])} />
                            </Grid>
                        </Grid>
                    ),
                },
            ]}
        />
    );

    return (
        <>
            <PageMeta title="Bus Stops" />
            {idsToDelete && (
                <ConfirmationDialog
                    keepMounted
                    open={!!idsToDelete}
                    onCancel={() => setIdsToDelete(null)}
                    onConfirm={() => doDelete(idsToDelete)}
                    title={idsToDelete.length === 1 ? 'Delete bus stop?' : `Delete ${idsToDelete.length} bus stops?`}
                    okButtonText="Delete"
                    cancelButtonText="Cancel"
                >
                    {idsToDelete.length === 1
                        ? 'Are you sure you want to delete this bus stop?'
                        : `Are you sure you want to delete ${idsToDelete.length} bus stops?`}
                </ConfirmationDialog>
            )}
            <FixedLinearProgress visibility={syncActive} />
            {isSmallScreen ? (
                <BusTourListMobileLayout
                    title="Bus Stops"
                    searchLabel="Search Bus Stops"
                    searchValue={query || ''}
                    onSearchChange={(e) => onQueryChange(e.target.value)}
                    actionButton={
                        <BusTourMobileActionButton
                            selectedCount={selectedIds.length}
                            canDelete={hasPerm('vehicle_fleet', 'delete')}
                            onDelete={() => setIdsToDelete(selectedIds)}
                            onAdd={() => history.push('/bus-tours/bus-stops/new')}
                            addLabel={addButtonLabel}
                            deleteLabel="Delete selected bus stops"
                        />
                    }
                >
                    {busStopsRecordList}
                </BusTourListMobileLayout>
            ) : (
                <Grid container direction="row" spacing={2} p={2}>
                    <Grid item xs={12} sx={{ pb: '0 !important' }}>
                        <PageHeading sx={{ mb: 1.5 }}>Bus Stops</PageHeading>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '0 !important' }}>
                        <Box sx={busTourListToolbarRowSx}>
                            <BusTourListSearchField
                                value={query || ''}
                                onChange={(e) => onQueryChange(e.target.value)}
                            />
                            {selectedIds.length > 0 ? (
                                <DeleteButton
                                    perm="vehicle_fleet.delete"
                                    onClick={() => setIdsToDelete(selectedIds)}
                                    sx={busTourListToolbarButtonSx}
                                >
                                    {selectedIds.length === 1 ? 'DELETE BUS STOP' : `DELETE ${selectedIds.length} BUS STOPS`}
                                </DeleteButton>
                            ) : (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => history.push('/bus-tours/bus-stops/new')}
                                    sx={busTourListToolbarButtonSx}
                                >
                                    {addButtonLabel}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        {busStopsRecordList}
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default BusStopList;
